import { Fragment, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ResourceHistoryEntry } from '@dispatch/shared/types';
import { groupFieldChanges, labelResolverFor } from './fieldChangeGroups';
import { formatFieldPath } from './fieldLabels';
import {
  formatTimestamp,
  isZoneListField,
  renderActor,
  visibleFieldChanges,
} from './historyFormat';
import { formatChangeCells, MemberNameResolver } from './membershipValues';

/**
 * Value length past which a cell collapses behind an expand control. A location's zone lists are the
 * only audit values that run long — thousands of characters for a wide territory — and rendered raw
 * they push the Field and Old columns off screen.
 */
const VALUE_COLLAPSE_THRESHOLD = 120;

const LIST_SEPARATOR = ', ';

/**
 * A value cell that collapses when long, with a VISIBLE expand control rather than a tooltip:
 * hover text is unreachable on touch, unreadable at this length, and cannot be selected or copied.
 * Expanding reveals the value in place as ordinary selectable text.
 *
 * WHETHER to collapse is keyed on the value's LENGTH, not on its field path, so any long value is
 * handled — a wide zone territory today, a long free-form metadata value tomorrow — with no list of
 * special-cased fields to keep in sync.
 *
 * Whether to claim an entry COUNT is a separate question, and that one cannot be answered from the
 * string: a comma-joined zone list and a comma-bearing sentence are indistinguishable. County names
 * contain spaces, so "segments look like tokens" fails too. So the caller states it via `isList`,
 * and the default is not to claim a count at all.
 */
function ExpandableValue({
  value,
  isList = false,
  testId,
}: {
  value: string;
  isList?: boolean;
  testId: string;
}) {
  const [expanded, setExpanded] = useState(false);

  if (value.length <= VALUE_COLLAPSE_THRESHOLD) {
    // The fragment is required, not incidental: a component's return type must be
    // Element | null, so returning the bare string fails as TS2786 ("cannot be used
    // as a JSX component"). Wrapping in an element instead would add a DOM node that
    // a short value never had.
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{value}</>;
  }

  // Cut on a separator so a collapsed zone list never shows half a zip code. Falls back to a hard
  // cut for a long value that is not a list (free-form metadata), where there is no safe boundary.
  const boundary = value.lastIndexOf(LIST_SEPARATOR, VALUE_COLLAPSE_THRESHOLD);
  const preview = value.slice(
    0,
    boundary > 0 ? boundary : VALUE_COLLAPSE_THRESHOLD
  );

  // Only meaningful for a real list. A truncation marker written by the audit builder is itself
  // joined with the separator, so it counts as one entry — deliberate, since the expanded view
  // lists it as its own line and the reader should see it.
  const entryCount = value.split(LIST_SEPARATOR).length;

  return (
    <Box>
      <Box component="span" sx={{ wordBreak: 'break-word' }}>
        {expanded ? value : `${preview}…`}
      </Box>
      <Box>
        <Button
          size="small"
          variant="text"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          data-testid={testId}
          sx={{ px: 0, minWidth: 0 }}
        >
          {expanded
            ? 'Show less'
            : isList
            ? `Show all ${entryCount}`
            : 'Show full value'}
        </Button>
      </Box>
    </Box>
  );
}

export type HistoryDetailModalProps = {
  /** The row whose full detail is shown; `null` keeps the modal closed. */
  entry: ResourceHistoryEntry | null;
  onClose: () => void;
  /**
   * Resolves a collection-member GUID — offices/teams/groups/roles, attribute
   * definitions, locations, certifications — to its display name, so a
   * membership row reads as "Downtown Branch (created)" rather than an
   * anonymous "created". Unresolvable members fall back to the raw audit value.
   */
  resolveName: MemberNameResolver;
};

/**
 * Full transaction view for a single history row. Unlike the grid summary,
 * this shows every field change — including the auto CreatedAt/UpdatedAt fields
 * the grid suppresses — so a "large" change is always fully viewable.
 */
export function HistoryDetailModal({
  entry,
  onClose,
  resolveName,
}: HistoryDetailModalProps) {
  // The machine-key rows are stripped here too, not just from the grid summary:
  // rendered, they would put a raw GUID on screen.
  const fieldChanges = visibleFieldChanges(entry?.fieldChanges);
  const groups = groupFieldChanges(fieldChanges, resolveName);

  return (
    <Dialog
      open={Boolean(entry)}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="history-detail-modal"
    >
      <DialogTitle>Change details</DialogTitle>
      <DialogContent dividers>
        {entry && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2">
                <strong>Date / Time:</strong> {formatTimestamp(entry.timestamp)}
              </Typography>
              <Typography variant="body2">
                <strong>Actor:</strong> {renderActor(entry.actor)}
              </Typography>
              <Typography variant="body2">
                <strong>Change Type:</strong> {entry.changeCategory}
              </Typography>
              <Typography variant="body2">
                <strong>Action:</strong> {entry.actionType}
              </Typography>
            </Box>

            {fieldChanges.length > 0 ? (
              <Table size="small" data-testid="history-detail-fields">
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Old</TableCell>
                    <TableCell>New</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((group) => (
                    <Fragment key={group.key}>
                      {group.heading && (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            sx={{ fontWeight: 600 }}
                            data-testid="history-detail-group-heading"
                          >
                            {group.heading}
                          </TableCell>
                        </TableRow>
                      )}
                      {group.changes.map((change, index) => {
                        const cells = formatChangeCells(change, resolveName);
                        return (
                          <TableRow key={`${change.fieldPath}-${index}`}>
                            <TableCell>
                              {formatFieldPath(
                                change.fieldPath,
                                labelResolverFor(group, resolveName)
                              )}
                            </TableCell>
                            <TableCell>
                              <ExpandableValue
                                value={cells.oldValue}
                                isList={isZoneListField(change.fieldPath)}
                                testId="history-detail-expand-old"
                              />
                            </TableCell>
                            <TableCell>
                              <ExpandableValue
                                value={cells.newValue}
                                isList={isZoneListField(change.fieldPath)}
                                testId="history-detail-expand-new"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No field-level detail for this change.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="history-detail-close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
